/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TFunction } from 'i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { useCancelUsers, usePagedUserQuery, UserRights } from '../../../services/user';
import Button, { ButtonToneProps } from '../../ui/Button/Button';
import Badge, { BadgeType } from '../../ui/Badge/Badge';
import { formatDate, formatTime, getCountryImageSource, resolveBadgeTypeForStatus } from '../../../utils/general';
import Icon from '../../ui/Icon/Icon';
import { DataView } from '../../ui/TableOverview/DataView';
import { useDataViewController } from '../../../hooks/useDataViewController';
import { SearchModels } from '../../ui/TableOverview/utils/SearchFieldRegistry';

const columnHelper = createColumnHelper<any>();
interface ColumnHelperProps {
  t: TFunction;
}

export interface ManageUsersProps {
  hasWriteAccess?: boolean;
}

const ManageUsers = ({ hasWriteAccess }: ManageUsersProps) => {
  const { t } = useTranslation();
  const cancelMutation = useCancelUsers();
  const navigate = useNavigate();

  const getColumns = ({ t }: ColumnHelperProps) => [
    columnHelper.accessor(row => row.name, {
      id: 'name',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      enableResizing: false,
      meta: {
        label: t('general.name'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-center gap-x-20 my-6">
          <span className="typo-text-14--bold text-wrap truncate">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.email, {
      id: 'email',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      enableResizing: false,
      meta: {
        label: t('general.email'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14 truncate">{info.getValue()}</span>,
    }),
    columnHelper.accessor(row => row.login, {
      id: 'login',
      size: 100,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.username'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14 truncate">{info.getValue()}</span>,
    }),
    columnHelper.accessor(row => row.lang, {
      id: 'lang',
      size: 100,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.language'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const value = info.getValue()?.split('_')[1].toUpperCase();
        const imageSrc = getCountryImageSource(value);
        return (
          value &&
          imageSrc && (
            <div className="flex items-center gap-x-[8px]">
              <Icon name={imageSrc || ''} />
              <span className="typo-text-14 text-full flex justify-center">{value}</span>
            </div>
          )
        );
      },
    }),
    columnHelper.accessor(row => row.login_date, {
      id: 'login_date',
      size: 150,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.last_login'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-center">
          {info.row.original.login_date ? (
            <div className="flex flex-col gap-2">
              <span className="typo-text-12 text-full truncate">{formatDate(info.row.original.login_date)}</span>
              <span className="typo-text-12 text-faded truncate">{formatTime(info.row.original.login_date)}</span>
            </div>
          ) : (
            <span className="typo-text-12 text-faded">{t('general.no_data')}</span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor(row => row.state, {
      id: 'state',
      size: 100,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.status'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const status = info.getValue();
        // The backend already returns res.users.state as a translated label
        // ("Confirmed"/"Bestätigt" vs. "Never Connected"), so we match the
        // confirmed labels across locales and render them in our success green.
        // Anything else (e.g. "Never Connected") keeps its neutral badge.
        const isConfirmed = ['confirmed', 'bestätigt', 'confirmado', '已确认'].includes(status?.toLowerCase() ?? '');
        return (
          <div className="flex">
            <span className="typo-text-14 text-full capitalize">
              <Badge
                showDot
                dotFilled
                className="max-w-min"
                label={status}
                expanded
                type={isConfirmed ? BadgeType.Successful : resolveBadgeTypeForStatus(status?.toLowerCase() ?? '')}
              />
            </span>
          </div>
        );
      },
    }),
  ];

  const controller = useDataViewController();

  return (
    <div className="flex flex-col w-full">
      <div className="flex w-full items-center justify-between mt-24 mb-12">
        <span className="typo-title-24">{t('general.team_members')}</span>
        {hasWriteAccess && (
          <Button
            title={t('general.add_new_user')}
            tone={ButtonToneProps.PRIMARY_BLUE}
            onClick={() => navigate('/settings/create')}
          />
        )}
      </div>
      <DataView<UserRights>
        controller={controller}
        modelKey={SearchModels.User}
        withSaveFilters={false}
        filterModelId="res.users"
        actions={[
          {
            title: t('general.view'),
            icon: 'eye',
            isRowClickAction: true,
            showInContextMenu: true,
            bulkSupport: false,
            onExecute: selected => {
              navigate(`/settings/edit/${selected[0].id}?view=true`);
            },
          },
          {
            title: t('general.edit'),
            icon: 'edit',
            isRowClickAction: false,
            showInContextMenu: true,
            bulkSupport: false,
            onExecute: selected => {
              navigate(`/settings/edit/${selected[0].id}`);
            },
          },
          {
            title: t('carrier_analytics.deactivate'),
            icon: 'close',
            isRowClickAction: false,
            showInContextMenu: true,
            bulkSupport: true,
            onExecute: selected => {
              const ids = selected.map(s => s.id).filter(id => id !== undefined && id !== null);
              if (ids.length > 0) {
                cancelMutation.mutateAsync({
                  params: { path: { model: 'res.users' } },
                  body: ids,
                });
              }
            },
          },
        ]}
        columns={getColumns({ t })}
        useDataQuery={usePagedUserQuery}
        applyContainer={false}
      />
    </div>
  );
};
export default ManageUsers;
